/**
 * conditionTemplate.js
 *
 * Jinja2 rendering of a generic CompareCondition (AutomationCondition) for
 * an HA template condition — shared by HomeAssistantGenerator (openHASP
 * object config) and HomeAssistantAutomationGenerator (HA automations).
 *
 *   left=ref('val'), op='==', right=literal('0')              → {{ val == 0 }}
 *   left=entity('light.x'), op='==', right=literal('on')      → {{ states('light.x') == 'on' }}
 *   left=entity('light.x', 'brightness'), op='>', right='128' → {{ state_attr('light.x', 'brightness') | float(0) > 128 }}
 */

const NUMERIC = /^-?\d+(\.\d+)?$/;
const ORDERING = new Set(['>', '<', '>=', '<=']);

// Single quotes: the surrounding value_template may be double-quoted.
const quote = text => `'${String(text).replace(/'/g, "\\'")}'`;

const isNumericLiteral = value => value?.kind === 'literal' && NUMERIC.test(value.value);

function renderOperand(value, numeric) {
  if (!value) return '';
  // Reference variables (val, text, …) appear unquoted in Jinja2
  if (value.kind === 'reference') return value.name;
  // Numeric literals stay unquoted; string literals (semantic option values) are quoted.
  if (value.kind === 'literal') return NUMERIC.test(value.value) ? value.value : quote(value.value);
  if (value.kind === 'entity') {
    const read = value.attribute
      ? `state_attr(${quote(value.entityId)}, ${quote(value.attribute)})`
      : `states(${quote(value.entityId)})`;
    // States are strings and an attribute may be none (e.g. brightness of a
    // light that is off): compare numerically with 0 for a missing value.
    return numeric ? `${read} | float(0)` : read;
  }
  return value.toString();
}

/**
 * @param {import('../ast/AutomationCondition.js').AutomationCondition} condition
 * @returns {string} e.g. "{{ val == 0 }}"
 */
export function renderConditionTemplate(condition) {
  const { left, operator, right } = condition;
  // An entity read without an entity can never hold.
  if ([left, right].some(value => value?.kind === 'entity' && !value.entityId)) return '{{ false }}';
  const numeric = ORDERING.has(operator) || isNumericLiteral(left) || isNumericLiteral(right);
  return `{{ ${renderOperand(left, numeric)} ${operator} ${renderOperand(right, numeric)} }}`;
}
